import React, { useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Heart, MapPin, Users, Shield, CheckCircle2, ArrowRight } from 'lucide-react';
import { CATEGORIES, CITIES, findSlug } from '@/data/seoSlugs';

const SITE = 'https://www.matchgroupid.com';

function useSeoTags({ title, description, canonical, keywords }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const setMeta = (name, content, isProp = false) => {
      const attr = isProp ? 'property' : 'name';
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };
    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      return el;
    };
    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonical, true);
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setLink('canonical', canonical);
    return () => { document.title = prevTitle; };
  }, [title, description, canonical, keywords]);
}

function buildContent(match) {
  if (match.kind === 'category') {
    const c = match.category;
    return {
      h1: `Cari Jodoh ${c.label} Indonesia`,
      title: `Cari Jodoh ${c.label} Indonesia — Match Group Indonesia`,
      description: `Platform perjodohan komunitas ${c.label} terpercaya di Indonesia. Bertemu jodoh serius sesama ${c.label} dari seluruh Indonesia — Jakarta, Surabaya, Medan, Bandung, Kupang, Bali, dan kota lainnya. Kurasi manual oleh admin MGI.`,
      keywords: `cari jodoh ${c.label.toLowerCase()}, cari jodoh ${c.label.toLowerCase()} indonesia, jodoh ${c.label.toLowerCase()}, biro jodoh ${c.label.toLowerCase()}, perjodohan ${c.label.toLowerCase()}, ${c.category.toLowerCase()}, mencari jodoh ${c.label.toLowerCase()} serius`,
      canonical: `${SITE}/jodoh/${c.slug}`,
      category: c,
      city: null,
    };
  }
  if (match.kind === 'city') {
    const city = match.city;
    return {
      h1: `Cari Jodoh di ${city.label}`,
      title: `Cari Jodoh di ${city.label} — Match Group Indonesia`,
      description: `Cari jodoh dan pasangan hidup di ${city.label}. Anggota terkurasi dari komunitas Buddhist, Katolik, Kristen, Islam, Hindu, Konghucu, dan Tenglang. Bergabung dengan Match Group Indonesia (MGI).`,
      keywords: `cari jodoh ${city.label.toLowerCase()}, jodoh ${city.label.toLowerCase()}, perjodohan ${city.label.toLowerCase()}, biro jodoh ${city.label.toLowerCase()}, cari pasangan ${city.label.toLowerCase()}, dating ${city.label.toLowerCase()}`,
      canonical: `${SITE}/jodoh/${city.slug}`,
      category: null,
      city,
    };
  }
  const c = match.category;
  const city = match.city;
  return {
    h1: `Cari Jodoh ${c.label} di ${city.label}`,
    title: `Cari Jodoh ${c.label} di ${city.label} — Match Group Indonesia`,
    description: `Perjodohan khusus komunitas ${c.label} di ${city.label}. Bertemu jodoh serius sesama ${c.label} yang tinggal di ${city.label}. Kurasi manual oleh admin Match Group Indonesia (MGI).`,
    keywords: `cari jodoh ${c.label.toLowerCase()} ${city.label.toLowerCase()}, jodoh ${c.label.toLowerCase()} ${city.label.toLowerCase()}, perjodohan ${c.label.toLowerCase()} ${city.label.toLowerCase()}, biro jodoh ${c.label.toLowerCase()} ${city.label.toLowerCase()}, ${c.category.toLowerCase()} ${city.label.toLowerCase()}`,
    canonical: `${SITE}/jodoh/${c.slug}-di-${city.slug}`,
    category: c,
    city,
  };
}

export default function SeoLandingPage() {
  const { slug } = useParams();
  const match = useMemo(() => findSlug(slug), [slug]);

  const content = useMemo(() => (match ? buildContent(match) : null), [match]);
  useSeoTags(content || { title: 'Match Group Indonesia', description: '', canonical: `${SITE}/`, keywords: '' });

  if (!match) return <Navigate to="/" replace />;

  const { h1, description, category, city } = content;

  return (
    <main className="min-h-screen bg-white" data-testid={`seo-page-${slug}`}>
      <header className="border-b" style={{ borderColor: 'var(--border-color)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/mgi-logo.jpeg" alt="Match Group Indonesia" width={32} height={32} className="rounded-full" />
            <span className="font-bold t-text">Match Group Indonesia</span>
          </Link>
          <Link to="/auth" data-testid="seo-cta-header" className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white text-sm font-semibold px-4 py-2 rounded-full">
            Login Member
          </Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-xs text-[#D98A94] font-bold tracking-widest uppercase mb-3 flex items-center gap-2">
          <Heart className="w-3.5 h-3.5" />
          Match Group Indonesia (MGI)
        </div>
        <h1 className="text-3xl sm:text-5xl font-bold t-text leading-tight" style={{ fontFamily: 'serif' }}>
          {h1}
        </h1>
        <p className="mt-4 text-base sm:text-lg t-text-secondary max-w-3xl">{description}</p>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href="https://wa.me/6281318719105?text=Halo%20MGI%2C%20saya%20ingin%20join%20member" target="_blank" rel="noreferrer"
            data-testid="seo-cta-wa"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold text-sm px-5 py-3 rounded-full">
            Join Member via WhatsApp
            <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/auth" data-testid="seo-cta-login" className="inline-flex items-center gap-2 bg-white border-2 border-[#D98A94] text-[#D98A94] hover:bg-[#D98A94] hover:text-white font-semibold text-sm px-5 py-3 rounded-full transition">
            Login Member
          </Link>
        </div>

        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          <div className="glass-card rounded-2xl p-5">
            <Shield className="w-8 h-8 text-[#D98A94] mb-3" />
            <div className="font-bold t-text mb-1">Kurasi Manual</div>
            <p className="text-sm t-text-secondary">Setiap profil diverifikasi admin. Bukan dating app massal.</p>
          </div>
          <div className="glass-card rounded-2xl p-5">
            <Users className="w-8 h-8 text-[#D98A94] mb-3" />
            <div className="font-bold t-text mb-1">Komunitas Serius</div>
            <p className="text-sm t-text-secondary">Anggota mencari jodoh untuk pernikahan, bukan sekadar chat.</p>
          </div>
          <div className="glass-card rounded-2xl p-5">
            <MapPin className="w-8 h-8 text-[#D98A94] mb-3" />
            <div className="font-bold t-text mb-1">Nasional & Internasional</div>
            <p className="text-sm t-text-secondary">Anggota tersebar di Indonesia, USA, Australia, Singapore, dan lainnya.</p>
          </div>
        </div>

        <section className="mt-12">
          <h2 className="text-2xl font-bold t-text mb-4">Kenapa Cari Jodoh di Match Group Indonesia?</h2>
          <ul className="space-y-3 text-sm t-text-secondary">
            <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#D98A94] flex-shrink-0 mt-0.5" /><span><b className="t-text">Profil terverifikasi</b> — WhatsApp diverifikasi, foto asli, admin memverifikasi status pernikahan.</span></li>
            <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#D98A94] flex-shrink-0 mt-0.5" /><span><b className="t-text">Kategori komunitas jelas</b> — Buddhist, Katolik, Kristen, Islam, Hindu, Konghucu, Tenglang, dan komunitas kawasan (PIK, BSD, Uwiw, Pelangi).</span></li>
            <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#D98A94] flex-shrink-0 mt-0.5" /><span><b className="t-text">Domisili spesifik</b> — dari Jakarta, Surabaya, Bandung, Medan, hingga Kupang, NTT, Bali, dan diaspora Indonesia.</span></li>
            <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#D98A94] flex-shrink-0 mt-0.5" /><span><b className="t-text">Kepatuhan UU PDP & UU ITE</b> — data Anda dilindungi. Hapus akun kapan saja via sistem tiket.</span></li>
            <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#D98A94] flex-shrink-0 mt-0.5" /><span><b className="t-text">Bebas biaya join</b> — daftar via WhatsApp admin, tidak ada langganan bulanan bertele-tele.</span></li>
          </ul>
        </section>

        {category && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold t-text mb-4">Cari Jodoh {category.label} di Berbagai Kota</h2>
            <div className="flex flex-wrap gap-2">
              {CITIES.slice(0, 20).map(c => (
                <Link key={c.slug} to={`/jodoh/${category.slug}-di-${c.slug}`}
                  className="text-xs px-3 py-1.5 rounded-full glass-card t-text-secondary hover:text-[#D98A94] hover:border-[#D98A94] transition">
                  Cari Jodoh {category.label} di {c.label}
                </Link>
              ))}
            </div>
          </section>
        )}

        {city && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold t-text mb-4">Cari Jodoh di {city.label} — Semua Komunitas</h2>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.filter(c => !['christian', 'muslim', 'chinese-indonesia'].includes(c.slug)).map(c => (
                <Link key={c.slug} to={`/jodoh/${c.slug}-di-${city.slug}`}
                  className="text-xs px-3 py-1.5 rounded-full glass-card t-text-secondary hover:text-[#D98A94] hover:border-[#D98A94] transition">
                  Cari Jodoh {c.label} di {city.label}
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <h2 className="text-2xl font-bold t-text mb-4">Kategori Perjodohan Populer</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CATEGORIES.slice(0, 12).map(c => (
              <Link key={c.slug} to={`/jodoh/${c.slug}`}
                className="glass-card rounded-xl p-4 hover:border-[#D98A94] transition group">
                <div className="font-bold t-text group-hover:text-[#D98A94] transition">Cari Jodoh {c.label}</div>
                <div className="text-xs t-text-muted mt-1">Komunitas {c.category}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12 text-center bg-gradient-to-br from-[#D98A94]/5 to-transparent rounded-2xl p-8">
          <h2 className="text-2xl font-bold t-text">Siap Menemukan Jodoh Anda?</h2>
          <p className="t-text-secondary mt-2 text-sm">Bergabung sekarang. Admin akan menghubungi Anda via WhatsApp untuk verifikasi.</p>
          <a href="https://wa.me/6281318719105?text=Halo%20MGI%2C%20saya%20ingin%20join%20member" target="_blank" rel="noreferrer"
            data-testid="seo-cta-final"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold text-base px-8 py-3.5 rounded-full mt-5">
            Join Member — WA 0813 1871 9105
            <ArrowRight className="w-4 h-4" />
          </a>
        </section>
      </section>

      <footer className="border-t mt-10" style={{ borderColor: 'var(--border-color)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 text-xs t-text-muted flex flex-wrap gap-2 justify-between">
          <div>© Match Group Indonesia · matchgroupid.com</div>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-[#D98A94]">Beranda</Link>
            <Link to="/ticket" className="hover:text-[#D98A94]">Sistem Tiket</Link>
            <Link to="/auth" className="hover:text-[#D98A94]">Login Member</Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
